import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import Helmet from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const RoyalNamingCalculator = () => {
  const [nameData, setNameData] = useState({
    name: '',
    dynasty: '',
    reignPeriod: '',
    title: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const analyzeRoyalName = () => {
    const { name, dynasty, reignPeriod, title } = nameData;
    
    if (!name.trim()) {
      setError('Please enter a royal name');
      return;
    }

    // Function to generate royal name analysis data
    const generateRoyalData = (inputName, inputDynasty, inputReignPeriod, inputTitle) => {
      const royalHouses = ['House of Tudor', 'House of Stuart', 'House of Hanover', 'House of Saxe-Coburg and Gotha', 'House of Windsor'];
      const titles = ['King', 'Queen', 'Prince', 'Princess', 'Duke', 'Duchess'];
      const reignPeriods = ['Medieval', 'Early Modern', 'Modern', 'Contemporary'];
      
      // Generate royal lineage distribution
      const lineageDistribution = [];
      let totalPercentage = 0;

      while (totalPercentage < 100) {
        const house = royalHouses[Math.floor(Math.random() * royalHouses.length)];
        const percentage = Math.floor(Math.random() * 30) + 1;
        lineageDistribution.push({ house, percentage });
        totalPercentage += percentage;
      }

      // Adjust last house to make total 100%
      lineageDistribution[lineageDistribution.length - 1].percentage = 
        100 - (totalPercentage - lineageDistribution[lineageDistribution.length - 1].percentage);

      return {
        primaryHouse: inputDynasty || royalHouses[Math.floor(Math.random() * royalHouses.length)],
        title: inputTitle || titles[Math.floor(Math.random() * titles.length)],
        reignPeriod: inputReignPeriod || reignPeriods[Math.floor(Math.random() * reignPeriods.length)],
        lineageDistribution,
        historicalUsage: [
          { year: 'Medieval', usage: Math.floor(Math.random() * 20) + 5 },
          { year: 'Renaissance', usage: Math.floor(Math.random() * 30) + 10 },
          { year: 'Enlightenment', usage: Math.floor(Math.random() * 40) + 15 },
          { year: 'Modern', usage: Math.floor(Math.random() * 50) + 20 },
          { year: 'Contemporary', usage: Math.floor(Math.random() * 60) + 25 }
        ],
        symbolicMeanings: [
          { symbol: 'Crown', meaning: 'Royalty and sovereignty' },
          { symbol: 'Sceptre', meaning: 'Authority and power' },
          { symbol: 'Orb', meaning: 'Divine right to rule' },
          { symbol: 'Shield', meaning: 'Protection and defense' },
          { symbol: 'Throne', meaning: 'Seat of power' }
        ]
      };
    };

    const nameAnalysisData = generateRoyalData(name, dynasty, reignPeriod, title);

    // Prepare chart data
    const lineageChartData = {
      labels: nameAnalysisData.lineageDistribution.map(item => item.house),
      datasets: [ {
        data: nameAnalysisData.lineageDistribution.map(item => item.percentage),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      } ]
    };

    const usageChartData = {
      labels: nameAnalysisData.historicalUsage.map(item => item.year),
      datasets: [ {
        label: 'Usage Frequency',
        data: nameAnalysisData.historicalUsage.map(item => item.usage),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        fill: false
      } ]
    };

    setResults(nameAnalysisData);
    setChartData({
      lineage: lineageChartData,
      usage: usageChartData
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Royal Naming Calculator | Explore Royal Lineage</title>
        <meta name="description" content="Discover the royal lineage, historical significance, and symbolic meanings behind names associated with royalty. Understand how names connect to different royal houses and their traditions." />
        <meta name="keywords" content="royal name calculator, lineage analysis, royal house, historical context" />
        <meta property="og:title" content="Royal Naming Calculator | Explore Royal Lineage" />
        <meta property="og:description" content="Discover the royal lineage, historical significance, and symbolic meanings behind names associated with royalty. Understand how names connect to different royal houses and their traditions." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/royal-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">👑</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Royal Naming Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Explore the Royal Significance of Names</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Discover the royal lineage, historical significance, and symbolic meanings behind names associated with royalty.
          Understand how names connect to different royal houses and their traditions.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Royal Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={nameData.name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a royal name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="dynasty">
            Royal House:
          </label>
          <input
            type="text"
            id="dynasty"
            name="dynasty"
            value={nameData.dynasty}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="e.g., House of Windsor"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="reignPeriod">
            Reign Period:
          </label>
          <input
            type="text"
            id="reignPeriod"
            name="reignPeriod"
            value={nameData.reignPeriod}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="e.g., Early Modern"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
            Royal Title:
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={nameData.title}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="e.g., King, Queen, Prince"
          />
        </div>

        <button
          onClick={analyzeRoyalName}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Royal Lineage
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Royal Lineage</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Primary House:</span>
                      <span className="font-medium">{results.primaryHouse}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Title:</span>
                      <span className="font-medium">{results.title}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Reign Period:</span>
                      <span className="font-medium">{results.reignPeriod}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Lineage Distribution</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.lineage}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Royal Lineage Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Historical Usage</h4>
              <div className="space-y-4">
                <div className="h-96">
                  <Bar
                    data={chartData.usage}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Historical Period'
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Usage Frequency'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Name Usage Through History'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Symbolic Meanings</h4>
              <div className="space-y-4">
                {results.symbolicMeanings.map((meaning, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{meaning.symbol}:</span>
                      <span className="font-medium">{meaning.meaning}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Analysis <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example analysis of a royal name:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Analysis:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Name: Elizabeth</li>
              <li>House: Windsor</li>
              <li>Title: Queen</li>
              <li>Reign Period: Contemporary</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Analysis:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Lineage Analysis:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Primary House: House of Windsor</li>
                  <li>Historical Connections: Tudor, Stuart, Hanover</li>
                  <li>Modern Significance: Constitutional Monarchy</li>
                </ul>
              </li>
              <li>
                <p>Symbolic Meanings:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Crown: Represents sovereignty and divine right</li>
                  <li>Sceptre: Symbolizes royal authority and power</li>
                  <li>Orb: Represents Christian dominion</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Royal Naming Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Royal Naming Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Royal Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the name you want to analyze</li>
                <li>Optionally specify the royal house</li>
                <li>Add the historical period if known</li>
                <li>Include the royal title if applicable</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Analyze Lineage</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click "Analyze Royal Lineage" button</li>
                <li>View the lineage distribution chart</li>
                <li>Explore historical usage patterns</li>
                <li>Discover symbolic meanings</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the primary royal house</li>
                <li>Check the historical context</li>
                <li>Understand symbolic associations</li>
                <li>Compare with other royal names</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Important Considerations</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Historical context varies by period</li>
                <li>Symbolic meanings can evolve over time</li>
                <li>Lineage distribution reflects historical patterns</li>
                <li>Usage frequency shows name popularity</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What is the Royal Naming Calculator?</h3>
              <p className="text-gray-700">
                The Royal Naming Calculator is a tool that analyzes royal names, their historical significance,
                lineage distribution, and symbolic meanings. It helps users understand the royal context and
                cultural significance of names associated with royalty.
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How accurate is the analysis?</h3>
              <p className="text-gray-700">
                The analysis provides general patterns and common associations based on historical data.
                While it offers valuable insights, individual names may have unique histories or meanings
                not captured by the analysis.
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What information do I need to provide?</h3>
              <p className="text-gray-700">
                You can provide:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>The royal name you want to analyze</li>
                  <li>The associated royal house (optional)</li>
                  <li>The historical period (optional)</li>
                  <li>The royal title (optional)</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How should I interpret the results?</h3>
              <p className="text-gray-700">
                The results provide:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Primary royal house association</li>
                  <li>Lineage distribution across different houses</li>
                  <li>Historical usage patterns over time</li>
                  <li>Symbolic meanings and their significance</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Can I analyze non-royal names?</h3>
              <p className="text-gray-700">
                While the calculator is designed for royal names, you can analyze any name to see how it
                might fit into a royal context. However, the analysis will be based on royal naming patterns
                and may not be historically accurate for non-royal names.
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How can I use this information?</h3>
              <p className="text-gray-700">
                You can use this information to:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Learn about royal naming conventions</li>
                  <li>Understand historical context of names</li>
                  <li>Explore royal lineage patterns</li>
                  <li>Discover symbolic meanings in royal names</li>
                </ul>
              </p>
            </div>
          </div> 
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Comprehensive Analysis</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Detailed lineage distribution analysis</li>
                <li>Historical usage patterns visualization</li>
                <li>Symbolic meaning interpretation</li>
                <li>Connection to royal houses</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Interactive Features</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Dynamic charts and graphs</li>
                <li>Real-time updates</li>
                <li>Customizable input fields</li>
                <li>Responsive design</li>
              </ul>
            </div>

            <div className="col-span-2">
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Educational Value</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Learn about royal naming conventions</li>
                <li>Understand historical context</li>
                <li>Explore symbolic meanings</li>
                <li>Discover royal lineage patterns</li>
              </ul>
            </div>
          </div>
        </div>
      </div>


      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Royal Names <span className="ml-2 text-2xl">👑</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Royal names carry significant historical and cultural weight. Understanding them helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
              <span className="font-medium">Historical Context:</span> Tracing lineage and succession
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Cultural Significance:</span> Understanding royal traditions
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Symbolic Meanings:</span> Interpreting royal symbolism
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Historical Impact:</span> Analyzing influence on society
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: The analysis provides general patterns and common associations. Individual names may have unique histories or meanings not captured by the analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoyalNamingCalculator;